import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Order } from "../types";
import { useExchangeRates } from "../context/ExchangeRateContext";

interface KanbanCardProps {
  order: Order;
  formatDate: (date?: string) => string;
  workshopDuration?: string;
  onEdit?: (orderId: string) => void;
  onDetail?: (orderId: string) => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({
  order,
  formatDate,
  workshopDuration,
  onEdit,
  onDetail,
}) => {
  const { usdRate, eurRate } = useExchangeRates();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: order.orderId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const getPriorityClass = (priority?: string) => {
    if (!priority) return "low";
    if (priority === "Yüksek" || priority === "High") return "high";
    if (priority === "Orta" || priority === "Medium") return "medium";
    return "low";
  };

  const calculateDuration = () => {
    const start = new Date(order.acceptanceDate);
    const now = new Date();
    const hours = Math.floor(
      (now.getTime() - start.getTime()) / (1000 * 60 * 60),
    );
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}g`;
    return `${hours}s`;
  };

  const formatPrice = () => {
    const total = (order.price || 0) * order.quantity;

    // Backend'den priceCurrency field'ı geliyor (PriceCurrency'den map ediliyor)
    // Önce priceCurrency'yi kontrol et, yoksa currency (eski alan) kullan
    const currency = order.priceCurrency || order.currency;

    // Döviz ise TL'ye çevir (kur varsa)
    let displayAmount = total;
    let displayCurrency = currency;

    if (currency === "USD" && usdRate) {
      displayAmount = total * usdRate;
      displayCurrency = "TRY";
    } else if (currency === "EUR" && eurRate) {
      displayAmount = total * eurRate;
      displayCurrency = "TRY";
    }

    const symbol =
      displayCurrency === "USD" ? "$" : displayCurrency === "EUR" ? "€" : "₺";

    // Tam tutar göster, binlik ayraçlı
    return `${symbol}${displayAmount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kcard ${isDragging ? "kcard--dragging" : ""}`}
    >
      {/* Hover Aksiyonları */}
      <div className="kcard__actions">
        <button
          className="kcard__btn kcard__btn--view"
          onClick={(e) => {
            e.stopPropagation();
            onDetail?.(order.orderId);
          }}
          title="Detay"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7z" />
          </svg>
        </button>
        <button
          className="kcard__btn kcard__btn--edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(order.orderId);
          }}
          title="Düzenle"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      </div>

      {/* Sürüklenebilir Alan */}
      <div className="kcard__body" {...attributes} {...listeners}>
        {/* Header */}
        <div className="kcard__header">
          <span className="kcard__model">{order.model?.modelCode || "-"}</span>
          {order.priority && (
            <span
              className={`kcard__priority kcard__priority--${getPriorityClass(order.priority)}`}
            >
              {order.priority}
            </span>
          )}
        </div>

        {/* Firma */}
        <div className="kcard__firm">{order.firm?.firmName || "—"}</div>

        {/* Meta Bilgiler */}
        <div className="kcard__meta">
          <span className="kcard__qty">{order.quantity} ad</span>
          <span className="kcard__divider">•</span>
          <span className="kcard__price">{formatPrice()}</span>
        </div>

        {/* Footer Detaylar */}
        <div className="kcard__footer">
          {order.operator && (
            <span className="kcard__tag">
              {order.operator.firstName?.charAt(0)}
              {order.operator.lastName?.charAt(0)}
            </span>
          )}
          {order.deadline && (
            <span className="kcard__date">{formatDate(order.deadline)}</span>
          )}
          <span className="kcard__time">⏱ {calculateDuration()}</span>
          {workshopDuration && (
            <span className="kcard__time">🏭 {workshopDuration}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
